import * as turf from '@turf/turf';
import moment from 'moment';
import { Feature, MultiPolygon, Point, Polygon, LineString } from '@turf/turf';
import { IActiveWindow, TimesheetMaterializer } from './airspace-schedules/TimesheetMaterializer';
import { AuthorizationFeatureCollection } from './AuthorizationFeatureCollection';
import { FeatureIntersector } from './FeatureIntersector';
import { GISDiffer } from './GISDiffer';
import { MergeSubdividedMission } from './MergeSubdividedMission';
import { IAdvisoryGeometry, IAdvisoryPropertiesV3, IAdvisoryTypeV3 } from './model/advisories/IAdvisoryPropertiesV3';
import { ILAANCStatusMeta } from './model/advisories/IAuthorizableLAANCAdvisoryProperties';
import { IClassGAdvisoryProperties } from './model/advisories/IClassGAdvisoryProperties';
import { IControlledAirspaceAdvisoryProperties } from './model/advisories/IControlledAirspaceAdvisoryProperties';
import { ILAANCAdvisoryProperties, ILAANCBlockReason } from './model/advisories/ILAANCAdvisoryProperties';
import { INationalParkAdvisoryProperties } from './model/advisories/INationalParkAdvisoryProperties';
import { INSUFRAdvisoryProperties } from './model/advisories/INSUFRAdvisoryProperties';
import { IPartTimeNSUFRAdvisoryProperties } from './model/advisories/IPartTimeNSUFRAdvisoryProperties';
import { IStadiumAdvisoryProperties } from './model/advisories/IStadiumAdvisoryProperties';
import { ITFRAdvisoryProperties } from './model/advisories/ITFRAdvisoryProperties';
import { IActiveAirspaceProperties } from './model/authority-tiles/IActiveAirspaceProperties';
import { IDCFRZProperties } from './model/authority-tiles/IDCFRZProperties';
import { ILAANCProperties } from './model/authority-tiles/ILAANCProperties';
import { INSUFRProperties } from './model/authority-tiles/INSUFRProperties';
import { IPartTimeNSUFRProperties } from './model/authority-tiles/IPartTimeNSUFRProperties';
import { ISpecialUseProperties } from './model/authority-tiles/ISpecialUseProperties';
import { ITFRProperties } from './model/authority-tiles/ITFRProperties';
import { ISubdividableMissionFeature } from './model/ISubdividableMissionFeature';
import { ISubdividableMissionProperties } from './model/ISubdividableMissionProperties';
import { PersistableMissionFeatureCollection } from './model/PersistableMissionFeatureCollection';
import { TilesClientLogger } from './support/TilesClientLogger';
import { IAirspaceBoundaryProperties } from './model/authority-tiles/IAirspaceBoundaryProperties';
import { LineIntersecter } from './support/LineIntersecter';
import { ISEAMSAdvisoryProperties } from './model/advisories/ISEAMSAdvisoryProperties';
import { ISO8601DateTimeString } from './model/ISO8601DateTimeString';

export type IActiveAirspaceFeature = Feature<Polygon | MultiPolygon, IActiveAirspaceProperties & {
    /**
     * if activeTimes is undefined, then always active
     * if activeTimes.length > 0, then it is active
     * if activeTimes.length === 0, then it is not active
     */
    activeTimes?: IActiveWindow[]
}>;

export class AuthorizationSubdivide {
    static inject = () => [
        GISDiffer,
        MergeSubdividedMission,
        TimesheetMaterializer,
        FeatureIntersector,
        TilesClientLogger,
        LineIntersecter
    ];
    constructor(
        private gisDiffer: GISDiffer,
        private mergeSubdividedMission: MergeSubdividedMission,
        private timesheetMaterializer: TimesheetMaterializer,
        private intersector: FeatureIntersector,
        private logger: TilesClientLogger,
        private lineIntersecter: LineIntersecter
    ) { }

    subdivide = ({ mission, authorization, initializedStatusMeta }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection, initializedStatusMeta: ILAANCStatusMeta }): PersistableMissionFeatureCollection => {
        const airspaceFeatures = authorization.getAirspaceFeatures()
            // sort so that B > C > D > E > E2 > E4
            .sort((a, b) => a.properties!.AP_CLASS.localeCompare(b.properties!.AP_CLASS))
            .map(airspace => this.enhanceWithActiveWindows({ airspace, mission: mission.properties }));

        const laancAdvisories = this.subdivideUASFMAdvisories({ mission, authorization, airspaceFeatures });

        const features: Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] = [
            ...laancAdvisories,
            ...this.subdivideControlledAirspaceAdvisories({ mission, laancAdvisories, airspaceFeatures }),
            ...this.getPointAdvisories({ mission, authorization, source: 'airports', advisoryType: () => 'advise'}),
            ...this.getNSUFRAdvisories({ mission, authorization }),
            ...this.getTemporaryFlightRestrictions({ mission, authorization }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'national-parks', advisoryType: () => 'advise'}),
            ...this.getPolygonAdvisories({
                source: 'airspace-boundary',
                mission,
                authorization,
                advisoryType: (props: IAirspaceBoundaryProperties) => props.adviseOnly ? 'advise' : 'block'
            }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'fria', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'rec-flyer-fixed-sites', advisoryType: () => 'advise'}),
            ...this.getLineAdvisories({ mission, authorization, source: 'military-training-routes', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'wilderness-areas', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'wilderness-areas-usfs', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'wilderness-areas-fws', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'wilderness-areas-blm', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'wildlife-refuges', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({ mission, authorization, source: 'cdfw-public-lands', advisoryType: () => 'advise' }),
            ...this.getPolygonAdvisories({
                source: 'special-use-airspace',
                mission,
                authorization,
                advisoryType: (props: ISpecialUseProperties) => ['P', 'R'].includes(props.TYPE_CODE) ? 'block': 'advise'
            }),
            ...this.getStadiumAdvisories({ mission, authorization }),
            ...this.subdivideSEAMSAdvisories({ mission, authorization }),
            ...this.getScheduledAirspaceAdvisories({ mission, authorization })
        ];

        // NOTE: this input has indirection due to a previous architecture
        // ideally we pass in the appopriate inputs more directly
        // but unit tests will also need to be refactored
        // currently broken, in that I need to pass in airspaceFeatures
        // so that uasfm can use it for schedule
        return this.mergeSubdividedMission.merge({
            type: 'FeatureCollection',
            properties: {
                mission,
                airspaceFeatures
            },
            features: features.filter(f => {
                const isPolygon = ['Polygon', 'MultiPolygon'].includes(f.geometry.type);
                if (!isPolygon){
                    return true;
                }
                return turf.area(f) > 10;
            })
        }, initializedStatusMeta);
    }

    private getScheduledAirspaceAdvisories = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        // TODO: type IScheduledAirspaceProperties
        const advisories: Feature<Polygon | MultiPolygon, IAdvisoryPropertiesV3>[] = [];

        for (const feature of authorization.getAirspaceFeatures().filter(f => f.properties.activeDuring)) {
            const intersection = this.intersector.getIntersection(mission, feature);
            if (intersection) {
                const advisory: Feature<Polygon | MultiPolygon, IAdvisoryPropertiesV3> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        source: 'scheduled-airspace',
                        advisoryType: 'advise'
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }

    private getLineAdvisories = ({ mission, authorization, source, advisoryType }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection; source: string; advisoryType: (props: any) => IAdvisoryTypeV3; }): Feature<IAdvisoryGeometry, INationalParkAdvisoryProperties>[] => {
        const advisories: Feature<IAdvisoryGeometry, any>[] = [];

        for (const feature of authorization.features.filter(f => f.properties.source === source) as Feature<LineString, any>[]) {

            const isAnyPortionOfLineWithinPolygon = this.lineIntersecter.isAnyPortionOfLineWithinPolygon({ line: feature, polygon: mission });

            if (isAnyPortionOfLineWithinPolygon){
                const advisory: Feature<IAdvisoryGeometry, any> = {

                    // ideally this would be a cropped line string
                    // but, that adds the complexity of MultiLineString (eg a single contiguous line, where both tails are in the radius, resulting in two lines that are not continuous )
                    // and we have nothing to gain from cropping it, ATM
                    ...feature,

                    properties: {
                        ...feature.properties,
                        advisoryType: advisoryType(feature.properties)
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }
    private getPointAdvisories = ({ mission, authorization, source, advisoryType }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection, source: string; advisoryType: (props: any) => IAdvisoryTypeV3;  }): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        const advisories: Feature<Point, any>[] = [];

        for (const feature of authorization.features.filter(f => f.properties.source === source) as Feature<Point, any>[]) {
            if (turf.booleanPointInPolygon(feature, mission)) {
                const advisory: Feature<Point, any> = {
                    ...feature,
                    properties: {
                        ...feature.properties,
                        advisoryType: advisoryType(feature.properties)
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }

    private getPolygonAdvisories = ({ mission, authorization, source, advisoryType }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection, source: string; advisoryType: (props: any) => IAdvisoryTypeV3;  }): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        const advisories: Feature<Polygon | MultiPolygon, any>[] = [];

        for (const feature of authorization.features.filter(f => f.properties.source === source) as Feature<Polygon | MultiPolygon, any>[]) {
            const intersection = this.intersector.getIntersection(mission, feature);
            if (intersection) {
                const advisory: Feature<Polygon | MultiPolygon, any> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        advisoryType: advisoryType(feature.properties)
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }

    private getNSUFRAdvisories = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        const advisories: Feature<Polygon | MultiPolygon, INSUFRAdvisoryProperties | IPartTimeNSUFRAdvisoryProperties>[] = [];

        for (const feature of this.getRelevantNSUFR({ mission, authorization })) {
            const intersection = this.intersector.getIntersection(mission, feature);
            if (intersection) {
                const advisory: Feature<Polygon | MultiPolygon> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        advisoryType: 'block'
                    }
                };

                advisories.push(advisory as Feature<Polygon | MultiPolygon, INSUFRAdvisoryProperties | IPartTimeNSUFRAdvisoryProperties>);
            }
        }

        return advisories;
    }

    private getTemporaryFlightRestrictions = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        const advisories: Feature<Polygon | MultiPolygon, ITFRAdvisoryProperties>[] = [];
        const tfrs = authorization.features.filter(f => f.properties!.source === 'temporary-flight-restrictions') as Feature<Polygon | MultiPolygon | Point, ITFRProperties>[];

        for (const feature of tfrs) {

            let polygonFeature: Feature<Polygon | MultiPolygon>;
            // DEPRECATED: this is here for backwards compatibility
            // but can be removed after 10/11/2022 (after we release tiles with polygons instead of point TFRs)
            if (feature.geometry.type === 'Point') {
                polygonFeature = turf.circle(feature.geometry as Point, 3, { steps: 25, units: 'nauticalmiles' });
            } else {
                polygonFeature = feature as Feature<Polygon | MultiPolygon>;
            }

            // is it active between our start and end?
            // (ie. it started before our end and didn't end before our start)
            const isActiveDuringMission = feature.properties!.effectiveStart < mission.properties.endTime && (feature.properties!.effectiveEnd === 'PERM' || feature.properties!.effectiveEnd > mission.properties.startTime);

            const intersection = this.intersector.getIntersection(mission, polygonFeature);
            if (intersection) {
                const advisory: Feature<Polygon | MultiPolygon, ITFRAdvisoryProperties> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        isPointTFR: feature.properties.wasPoint

                            // DEPRECATED: this is here for backwards compatibility
                            // but can be removed after 10/11/2022 (after we release tiles with polygons instead of point TFRs)
                            || feature.geometry.type === 'Point',

                        // they should always have an advisoryType
                        // but the first pass didn't so this makes it safe during the transition
                        advisoryType: isActiveDuringMission ? (feature.properties.advisoryType || 'block') : 'advise',
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }


    private getRelevantNSUFR = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }): Feature<Polygon | MultiPolygon, INSUFRProperties | IPartTimeNSUFRProperties | IDCFRZProperties>[] => {
        const nsurf = authorization.features.filter(f => f.properties!.source === 'nsufr') as Feature<Polygon | MultiPolygon, INSUFRProperties>[];
        const partTimeNSUFR = authorization.features
            .filter(f => f.properties!.source === 'parttime-nsufr')
            .filter(f => {
                // NOTE: ideally we can refactor to some commonality between this and TimesheetMaterializer
                // which both now have to determine if a window is active against other windows
                const nsufr = f.properties as IPartTimeNSUFRProperties;
                // if is is active between our start and end
                // ... if it started before our end and didn't end before our start
                return nsufr.ACTIVETIME < mission.properties.endTime && nsufr.ENDTIME > mission.properties.startTime;

            }) as Feature<Polygon | MultiPolygon, IPartTimeNSUFRProperties>[];
        return [
            ...nsurf,
            ...partTimeNSUFR,
        ];
    }

    private subdivideControlledAirspaceAdvisories = ({ mission, laancAdvisories, airspaceFeatures }: { mission: ISubdividableMissionFeature, laancAdvisories: Feature<Polygon | MultiPolygon>[], airspaceFeatures: IActiveAirspaceFeature[] }): Feature<IAdvisoryGeometry, IControlledAirspaceAdvisoryProperties | IClassGAdvisoryProperties>[] => {
        let radiusUnknown = JSON.parse(JSON.stringify(mission));

        // crop out laancAdvisories because that airspace is accounted for and should not be blocked
        for (const feature of laancAdvisories) {
            if (!radiusUnknown) {
                continue;
            }

            const intersection = this.intersector.getIntersection(radiusUnknown, feature);
            if (intersection) {
                radiusUnknown = this.gisDiffer.diff({ clipPolygonFrom: radiusUnknown, clipPolygon: intersection });
            }
        }
        const advisories: Feature<Polygon | MultiPolygon, IControlledAirspaceAdvisoryProperties | IClassGAdvisoryProperties>[] = [];

        for (const feature of airspaceFeatures) {
            // TODO: this is temporary DX solution til we prioritize the UX
            // see https://merakisolutionsinc.slack.com/archives/C02S2G6H3FZ/p1659638718353909?thread_ts=1659637694.239589&cid=C02S2G6H3FZ
            if (feature.properties.activeDuring) {
                this.logger.info(`subdividing controlled airspace, found ${feature.properties.IDENT} nearby which has schedule`);
                this.logger.info(JSON.stringify(feature.properties.activeDuring));
                this.logger.info('and active times');
                this.logger.info(JSON.stringify(feature.properties.activeTimes));
            }

            // if we have no radius left to match to features, then skip this airspace feature
            if (!radiusUnknown) continue;

            const isActive = !feature.properties.activeTimes || feature.properties.activeTimes.length > 0;
            if (!isActive) continue;

            const intersection = this.intersector.getIntersection(radiusUnknown, feature);
            if (intersection) {
                radiusUnknown = this.gisDiffer.diff({ clipPolygonFrom: radiusUnknown, clipPolygon: intersection });

                const controlledAdvisory: Feature<Polygon | MultiPolygon, IControlledAirspaceAdvisoryProperties> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        advisoryType: 'block'
                    }
                };

                advisories.push(controlledAdvisory);
            }
        }

        if (radiusUnknown) {
            const classGFeature: Feature<Polygon, IClassGAdvisoryProperties> = {
                ...radiusUnknown,
                properties: {
                    advisoryType: 'advise',
                    source: 'airspace+class-g',
                    AP_CLASS: 'G'
                }
            };

            advisories.push(classGFeature);
        }

        return advisories;
    }

    private enhanceWithActiveWindows = ({ mission, airspace }: { mission: ISubdividableMissionProperties, airspace: Feature<Polygon | MultiPolygon, IActiveAirspaceProperties> }): IActiveAirspaceFeature => {
        if (!airspace.properties.activeDuring) {
            return airspace;
        }

        const activeTimes = this.timesheetMaterializer.materialize({
            windowToMaterialize: {
                startTime: mission.startTime,
                endTime: mission.endTime
            },
            timesheets: airspace.properties.activeDuring
        });

        return {
            ...airspace,
            properties: {
                ...airspace.properties,
                activeTimes
            }
        };
    }

    // TODO: stadiums should just follow the same pattern we use for operational awareness
    // big change though, so not a priority
    private getStadiumAdvisories = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }): Feature<Polygon | MultiPolygon, IStadiumAdvisoryProperties>[] => {
        const stadiums = authorization.getStadiumPoints();

        const stadiumRadiusFeatures = stadiums.map(point => {
            return turf.circle(point.geometry.coordinates, 3 * 1.1508, { units: 'miles', steps: 25, properties: point.properties });
        });

        const stadiumIntersections: Feature<Polygon | MultiPolygon, IStadiumAdvisoryProperties>[] = [];
        for (const stadiumRadius of stadiumRadiusFeatures) {
            const intersection = this.intersector.getIntersection(mission, stadiumRadius);
            if (intersection) {
                stadiumIntersections.push({
                    ...intersection,
                    properties: {
                        ...stadiumRadius.properties,
                        advisoryType: 'advise'
                    }
                });
            }
        }

        return stadiumIntersections;
    }

    subdivideSEAMSAdvisories = ({ mission, authorization }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection }) => {

        const advisories: Feature<Polygon | MultiPolygon, ISEAMSAdvisoryProperties>[] = [];

        for (const feature of authorization.getSEAMS()) {
            const intersection = this.intersector.getIntersection(mission, feature);

            if (intersection) {
                const missionProps = mission.properties;
                const isVenueActive = feature.properties.IS_ACTIVE === 1;

                const eventStartTimes: ISO8601DateTimeString[] = [];
                const eventEndTimes: ISO8601DateTimeString[] = [];

                feature.properties.eventStartTimes.forEach((start, idx) => {
                    const end = feature.properties.eventEndTimes[idx];
                    const nextStart = feature.properties.eventStartTimes[idx + 1];
                    const overrun = (isVenueActive && moment().isAfter(end) && (!nextStart || moment().isBefore(nextStart)));
                    const tfrStart = moment(start).subtract(1, 'hour');
                    const tfrEnd = moment(overrun ? moment() : end).add(1, 'hour');
                    const timesOverlap = (moment(missionProps.startTime).isBefore(tfrEnd) && moment(missionProps.endTime).isAfter(tfrStart));
                    if (timesOverlap) {
                        eventStartTimes.push(start);
                        eventEndTimes.push(end);
                    }
                });

                const advisory: Feature<Polygon | MultiPolygon, any> = {
                    ...intersection,
                    properties: {
                        ...feature.properties,
                        eventStartTimes,
                        eventEndTimes,
                        advisoryType: eventStartTimes.length > 0 ? 'block' : 'advise'
                    }
                };

                advisories.push(advisory);
            }
        }

        return advisories;
    }

    private subdivideUASFMAdvisories = ({ mission, authorization, airspaceFeatures }: { mission: ISubdividableMissionFeature, authorization: AuthorizationFeatureCollection, airspaceFeatures: IActiveAirspaceFeature[] }): Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[] => {

        let radiusUnknown = JSON.parse(JSON.stringify(mission));

        const uasfmAirspaceFeatures = authorization.features.filter(f => f.properties!.source === 'uasfm+airspace') as Feature<Polygon, ILAANCProperties>[];

        const laancAdvisories: Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[] = [];

        for (const feature of uasfmAirspaceFeatures as Feature<Polygon | MultiPolygon, ILAANCProperties>[]) {
            if (!radiusUnknown) {
                continue;
            }

            const intersection = this.intersector.getIntersection(radiusUnknown, feature);
            if (intersection) {
                const airspace = airspaceFeatures.find(a => a.properties.GLOBAL_ID === feature.properties.AIRSPACE_GLOBAL_ID)!;

                if (!airspace) {
                    throw new Error(`UASFM ${feature.properties.UASFM_GLOBAL_ID} makes reference to airspace we could not find: ${feature.properties.AIRSPACE_GLOBAL_ID}`);
                }

                const isActive = !airspace.properties.activeTimes || airspace.properties.activeTimes.length > 0;
                if (!isActive) continue;

                radiusUnknown = this.gisDiffer.diff({ clipPolygonFrom: radiusUnknown, clipPolygon: intersection });
                const laancAdvisory: Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties> = {
                    ...intersection,
                    properties: this.getLAANCAdvisoryProperties({ properties: feature.properties, isLicensed: mission.properties.isLicensed })
                };

                laancAdvisories.push(laancAdvisory);
            }
        }

        return laancAdvisories;
    }

    private getLAANCAdvisoryProperties = ({ properties, isLicensed }: { properties: ILAANCProperties, isLicensed: boolean }): ILAANCAdvisoryProperties => {
        const blockReasons: ILAANCBlockReason[] = [];

        const enabledFor = (properties.UASFM_Enabled || []);

        if (!properties.UASFM_LAANC) {
            blockReasons.push('laanc-inactive');
        } else if (properties.CEILING === 0) {
            blockReasons.push('laanc-not-aa');
        } else if (isLicensed && enabledFor.includes('107-AA') == false) {
            blockReasons.push('laanc-not-107-aa');
        } else if (!isLicensed && enabledFor.includes('44809-AA') == false) {
            blockReasons.push('laanc-not-44809-aa');
        }

        return {
            ...properties,
            advisoryType: blockReasons.length > 0 ? 'block' : 'advise',
            blockReasons
        };
    }
}
